import {getCurrentLayout} from "../bvcomponents/appLayout";

// decor is scenery rather than something being quoted, so it is left out of the documents.
// the data carries both spellings of the group name, see skipRequestsFor in server-api.js and
// the Globals part loading
const EXCLUDED_GROUP_NAMES = ["Decor", "Decore"];

// QuoteDocumentFeatures column limits, see docs/quote-document-features.md
export const DOCUMENT_FEATURE_GROUP_NAME_MAX_LENGTH = 256;
export const DOCUMENT_FEATURE_DISPLAY_NAME_MAX_LENGTH = 512;

/**
 * the server fails the whole save on a too long value instead of truncating it, so we cut it here
 * @param value {any}
 * @param maxLength {number}
 * @returns {string}
 */
const getNormalizedFeatureValue = (value, maxLength) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim().substring(0, maxLength);
};

/**
 * returns a document feature object, null when it is empty (the server skips such entries anyway)
 * @param groupName {any}
 * @param displayName {any}
 * @returns {{groupName: string, displayName: string}|null}
 */
const getQuoteDocumentFeature = (groupName, displayName) => {
  const feature = {
    groupName: getNormalizedFeatureValue(groupName, DOCUMENT_FEATURE_GROUP_NAME_MAX_LENGTH),
    displayName: getNormalizedFeatureValue(displayName, DOCUMENT_FEATURE_DISPLAY_NAME_MAX_LENGTH),
  };
  if (!feature.groupName && !feature.displayName) {
    return null;
  }
  return feature;
};

/**
 * returns the document features of the given db features, de-duplicated and sorted,
 * as the server doesn't guarantee the order
 * @param dbFeatures {array} the db features the document should carry
 * @returns {[{groupName: string, displayName: string}]}
 */
export const getDocumentFeatures = (dbFeatures) => {
  const usedFeatureKeys = {};
  return dbFeatures
    .filter(dbFeature => !!dbFeature)
    .map(dbFeature => getQuoteDocumentFeature(dbFeature.groupName, dbFeature.displayName))
    .filter(feature => {
      if (!feature) {
        return false;
      }
      const featureKey = feature.groupName + "|" + feature.displayName;
      if (usedFeatureKeys[featureKey]) {
        return false;
      }
      usedFeatureKeys[featureKey] = true;
      return true;
    })
    .sort((a, b) => a.groupName.localeCompare(b.groupName) || a.displayName.localeCompare(b.displayName));
};

/**
 * returns the features the visualizer is displaying, to be saved with the quote documents - which
 * is not the same as what the cart is priced on.
 * The active features of every group paired with the layers actually on the canvas, the same way
 * createThumbnailsCanvas decides what the room shows. getActiveFeatures(false) is not enough on
 * its own: it narrows the list down to the qt categories, which drops the walls, the floor and the
 * center island countertop, so every group is asked for and the layers decide what is displayed.
 * The decor groups are left out, they are not being quoted.
 * @returns {[{groupName: string, displayName: string}]}
 */
export const getVisualizerDocumentFeatures = () => {
  const layout = getCurrentLayout();
  if (!layout) {
    return [];
  }
  const displayedLayers = layout.getLayers(false, true); // no placeholders, even before the first render
  return getDocumentFeatures(layout.getActiveFeatures(true)
    .filter(activeFeature => displayedLayers.some(layer => layer.partId === activeFeature.partId))
    .map(activeFeature => activeFeature.cartFeature && activeFeature.cartFeature.dbFeature)
    .filter(dbFeature => dbFeature && !EXCLUDED_GROUP_NAMES.includes(dbFeature.groupName)));
};
